import React, { createContext, useState, useEffect, useCallback } from "react";
import { auth } from "../../config/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useHttpClient } from "../hooks/http-hook";
import { multiFactor } from "firebase/auth";

export const AuthContext = createContext({
	isLoggedIn: false,
	userId: null,
	token: null,
	isSuperAdmin: false,
	isProducer: false,
	isInOrganization: false,
	isAuthReady: false,
	orgRole: null,
	orgName: null,
	memberships: [],
	organizations: [],
	currentOrganization: null,
	firstName: null,

	login: () => {},
	logout: () => {},
});

export const AuthProvider = ({ children }) => {
	const [token, setToken] = useState(null);
	const [userId, setUserId] = useState(null);
	const [isSuperAdmin, setIsSuperAdmin] = useState(false);
	const [isProducer, setIsProducer] = useState(false);
	const [isAuthReady, setIsAuthReady] = useState(false);
	const [isInOrganization, setIsInOrganization] = useState(false);
	const [orgRole, setOrgRole] = useState(null);
	const [orgName, setOrgName] = useState(null);
	const [memberships, setMemberships] = useState([]);
	const [organizations, setOrganizations] = useState([]);
	const [currentOrganization, setCurrentOrganization] = useState(null);
	const [hasPhoneMfa, setHasPhoneMfa] = useState(false);
	const [requiresMFA, setRequiresMFA] = useState(false);
	const [firstName, setFirstName] = useState(null);
	const [activeSubscription, setActiveSubscription] = useState(false);

	const { sendRequest } = useHttpClient();

	const refreshUserData = useCallback(async () => {
		if (auth.currentUser) {
			try {
				await auth.currentUser.reload();

				const multiFactorUser = multiFactor(auth.currentUser);

				const enrolledFactors = multiFactorUser.enrolledFactors || [];
				const hasPhoneMfaEnabled = enrolledFactors.some(
					(factor) => factor.factorId === "phone"
				);

				setHasPhoneMfa(hasPhoneMfaEnabled);
			} catch (error) {
				console.error("Error refreshing user data:", error);
				setHasPhoneMfa(false);
			}
		}
	}, []);

	const login = useCallback(
		async (uid, token) => {
			setToken(token);
			setUserId(uid);
			localStorage.setItem(
				"userData",
				JSON.stringify({ userId: uid, token: token })
			);

			if (auth.currentUser) {
				try {
					await auth.currentUser.reload();

					const multiFactorUser = multiFactor(auth.currentUser);

					const enrolledFactors = multiFactorUser.enrolledFactors || [];
					const hasPhoneMfaEnabled = enrolledFactors.some(
						(factor) => factor.factorId === "phone"
					);
					setHasPhoneMfa(hasPhoneMfaEnabled);

					const response = await sendRequest("/api/roles", "GET", null, {
						Authorization: "Bearer " + token,
					});
					const data = response.data;
					//NEED ENTITLEMENTS FROM STRIPE HERE
					setIsSuperAdmin(data.isSuperAdmin);
					setIsProducer(data.isProducer);
					setIsInOrganization(data.isInOrganization);
					setMemberships(data.memberships);
					setFirstName(data.firstName);

					if (data.isInOrganization && data.memberships.length > 0) {
						setOrganizations(data.memberships);
						// Use first memebership to set orgRole and orgName
						const defaultOrg = data.memberships[0];
						setOrgRole(defaultOrg.role);
						setOrgName(defaultOrg.orgName);
						setRequiresMFA(defaultOrg.requiresMFA);
						//set activesubscription based on defaultOrg.active = true/false
						setActiveSubscription(defaultOrg.active);
                        //setOrgRole(null);
						//setOrgName(null);
					} else {
						setOrgRole(null);
						setOrgName(null);
						setOrganizations([]);
						setCurrentOrganization(null);
						setActiveSubscription(false);
					}
				} catch (error) {
					console.error("Error fetching user roles from backend: ", error);
					setIsSuperAdmin(false);
					setIsProducer(false);
					setIsInOrganization(false);
					setOrgRole(null);
					setOrgName(null);
					setActiveSubscription(false);
					setOrganizations([]);
					setCurrentOrganization(null);
					setHasPhoneMfa(false);
					setRequiresMFA(false);
					setFirstName(null);
				}
			} else {
				setIsSuperAdmin(false);
				setIsProducer(false);
				setIsInOrganization(false);
				setOrgRole(null);
				setOrgName(null);
				setActiveSubscription(false);
				setOrganizations([]);
				setCurrentOrganization(null);
				setHasPhoneMfa(false);
				setRequiresMFA(false);
				setFirstName(null);
			}

			setIsAuthReady(true);
		},
		[sendRequest]
	);

	const logout = useCallback(() => {
		setToken(null);
		setUserId(null);
		setIsSuperAdmin(false);
		setIsProducer(false);
		setIsInOrganization(false);
		setOrgRole(null);
		setOrgName(null);
		setActiveSubscription(false);
		setFirstName(null);
		localStorage.removeItem("userData");
		setIsAuthReady(true);
	}, []);

	const forceTokenRefreshAndLogin = useCallback(
		async (storedData) => {
			const user = auth.currentUser;
			if (user) {
				try {
					const newToken = await user.getIdToken(true);
					await login(user.uid, newToken);
				} catch (error) {
					console.error("Error refreshing token:", error);
					await login(storedData.userId, storedData.token);
				}
			} else {
				logout();
			}
		},
		[login, logout]
	);

	const switchOrganization = (orgId) => {
		if (orgId === null || orgId === "none") {
			setCurrentOrganization(null);
			setOrgRole(null);
			setOrgName(null);
		} else {
			const selectedOrg = memberships.find((org) => org.orgId === orgId);
			if (selectedOrg) {
				setCurrentOrganization(selectedOrg);
				setOrgRole(selectedOrg.role);
				setOrgName(selectedOrg.orgName);
				setActiveSubscription(selectedOrg.active);
				console.log("Switched to organization:", selectedOrg);
			} else {
				console.error("Organization not found:", orgId);
			}
		}
	};

	useEffect(() => {
		const storedData = JSON.parse(localStorage.getItem("userData"));
		const unsubscribe = onAuthStateChanged(auth, async (user) => {
			if (user && storedData && storedData.token) {
				forceTokenRefreshAndLogin(storedData);
			} else {
				setIsAuthReady(true);
			}
		});
		return () => unsubscribe();
	}, [forceTokenRefreshAndLogin]);

	return (
		<AuthContext.Provider
			value={{
				isLoggedIn: !!token,
				userId: userId,
				token: token,
				isSuperAdmin: isSuperAdmin,
				isProducer: isProducer,
				isInOrganization: isInOrganization,
				isAuthReady: isAuthReady,
				orgRole: orgRole,
				orgName: orgName,
				memberships: memberships,
				organizations: organizations,
				currentOrganization: currentOrganization,
				hasPhoneMfa: hasPhoneMfa,
				requiresMFA: requiresMFA,
				firstName: firstName,
				activeSubscription: activeSubscription,
				switchOrganization: switchOrganization,
				login: login,
				logout: logout,
				refreshUserData: refreshUserData,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};
